import Checkbox from "@material-ui/core/Checkbox";
import Alert from "@material-ui/lab/Alert";
import usePeriodFromString from "backend/hooks/model/usePeriodFromString";
import useScorecardElements from "backend/hooks/model/useScorecardElements";
import useScorecardHierarchy from "backend/hooks/model/useScorecardHierarchy";
import useAllWorkflowInstances from "backend/hooks/workflows/useAllWorkflowInstances";
import useDeleteWorkflowInstance from "backend/hooks/workflows/useDeleteWorkflowInstance";
import useStartElementWorkflow from "backend/hooks/workflows/useStartElementWorkflow";
import useSubmitElementValues from "backend/hooks/workflows/useSubmitElementValues";
import { ICardElementWithChildren } from "backend/model/model";
import * as wf from "client-api-workflow";
import {
  GETWorkflowInstanceResultData,
  WFConfigParams,
} from "client-api-workflow/types";
import WorkflowAdminToolbar from "components/toolbars/WorkflowAdminToolbar";
import AdminButton from "design/components/buttons/AdminButton";
import PeriodChip from "design/components/chips/PeriodChip";
import ValueInputForm from "design/components/forms/ValueInputForm";
import GenericModal from "design/components/popups/GenericModal";
import MeasureTable from "design/components/tables/MeasureTable";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import ElementTreeView from "design/components/trees/ElementTreeView";
import RowView from "design/layouts/RowView";
import StackView from "design/layouts/StackView";
import TreeViewLeftPanel from "design/layouts/TreeViewLeftPanel";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import Page from "design/pages/Page";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import Loader from "qpr-base/ui/components/Loader";
import React, { FunctionComponent } from "react";
import { getTranslatedAttribute } from "qpr-base/tplutils/utils";
interface ConfigurableReportsProps {}

const WorkflowAdmin: FunctionComponent<ConfigurableReportsProps> = () => {
  const ctx = useAppContext();

  const [rootItem, setRootItem] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);

  const { data: scorecardHierarchy, isLoading: isScorecardHierarchyLoading } =
    useScorecardHierarchy(ctx.modelId);

  const { isLoading: isStartingWorkflows, mutate: startWorkflows } =
    useStartElementWorkflow();
  const { isLoading: isDeletingWorkflows, mutate: deleteWorkflowInstance } =
    useDeleteWorkflowInstance();

  const { data: allInstances, isLoading: isAllInstancesLoading } =
    useAllWorkflowInstances("value", ctx.period || "");

  const [action, setAction] = React.useState("");
  const [currentwfconfig, setcurrentwfconfig] = React.useState("");

  const [openedItem, setOpenedItem] = React.useState<
    undefined | ICardElementWithChildren
  >(undefined);

  const [configNames, setConfigNames] = React.useState<WFConfigParams[]>([]);

  const saveValues = useSubmitElementValues();
  const [selectedItems, setSelectedItems] = React.useState<
    ICardElementWithChildren[]
  >([]);

  const { data: subElements, isLoading: isSubElementsLoading } =
    useScorecardElements(rootItem?.topelement || "", ctx.period || "");

  const currentPeriod = usePeriodFromString();

  const listItems = React.useMemo(() => {
    return (
      subElements
        ?.filter((el) => el.typename === "KPI" || el.typename === "Initiative")
        .sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [subElements]);

  const instancesPerElement = React.useMemo(() => {
    const obj: Record<
      string,
      Array<GETWorkflowInstanceResultData & ICardElementWithChildren>
    > = {};
    if (allInstances) {
      allInstances.forEach((inst) => {
        if (!obj[inst.meaId]) obj[inst.meaId] = [];
        obj[inst.meaId].push(inst);
      });
    }
    return obj;
  }, [allInstances, isDeletingWorkflows, saveValues.isLoading]);

  const toggleItem = (item: ICardElementWithChildren) => {
    setSelectedItems((items) => {
      if (items.find((it) => it.id === item.id)) {
        return items.filter((i) => i.id !== item.id);
      }
      return [...items, item];
    });
  };

  React.useEffect(() => {
    if (scorecardHierarchy && !isScorecardHierarchyLoading)
      setRootItem(scorecardHierarchy?.[0]);
  }, [scorecardHierarchy]);

  React.useEffect(() => {
    const loadConfigNames = async () => {
      const configurations = await wf.getWorkflowConfigurations();
      setConfigNames(configurations);
    };

    loadConfigNames();
  }, []);

  if (
    !rootItem ||
    isStartingWorkflows ||
    isScorecardHierarchyLoading ||
    isAllInstancesLoading ||
    isSubElementsLoading ||
    isDeletingWorkflows ||
    saveValues.isLoading
  ) {
    return (
      <>
        <WorkflowAdminToolbar /> <Loader />
      </>
    );
  }

  const getElementInstances = (
    id: string
  ): (GETWorkflowInstanceResultData & ICardElementWithChildren)[] => {
    try {
      return instancesPerElement[id];
    } catch (e) {}
    return [];
  };

  return (
    <Page
      isLoading={
        isScorecardHierarchyLoading ||
        isAllInstancesLoading ||
        isDeletingWorkflows ||
        saveValues.isLoading
      }
      Toolbar={WorkflowAdminToolbar}
    >
      <GenericModal
        open={!!openedItem}
        onClose={() => {
          setOpenedItem(undefined);
        }}
      >
        <h1>
          {openedItem?.name} {openedItem?.workflowInstance?.period}{" "}
        </h1>
        {false ? (
          <></>
        ) : (
          <ValueInputForm
            buttons={[
              {
                tag: "submit",
                style: "submit",
                text: "Submit",
                onClick: (values) => {
                  console.log("Submit", values);
                  setOpenedItem(undefined);
                  saveValues.mutate({
                    fields: values,
                  });
                },
              },
            ]}
            key={`${openedItem?.workflowInstance?.wfInstanceId}`}
            element={openedItem!}
            childElements={[]}
            onIntermediateChange={(values) => {
              console.log(values);
            }}
            fields={[
              {
                series: "ACT",
                element: openedItem,
                inputType: "string",
                isDisabled:
                  openedItem?.workflowInstance?.currentState !== "dataentry",
              },
              {
                title: "Actions and Recommendations",
                element: openedItem,
                inputType: "comment",
                comments: [],
                initialValue: "",
              },
            ]}
          />
        )}
      </GenericModal>
      <StackView gap="0px">
        <RowView partWidths={["300px", "900px"]} justifyContent="flex-start">
          <TreeViewLeftPanel>
            <ElementTreeView
              selectedItem={rootItem}
              items={scorecardHierarchy || []}
              onSelected={(item) => {
                setRootItem(item);
              }}
            />
          </TreeViewLeftPanel>
          <WhiteBgArea width="100%">
          <ExecutiveDashboardTitle>
              {getTranslatedAttribute(rootItem, "Name", ctx.language)}
            </ExecutiveDashboardTitle>

            {!listItems ||
            listItems.length === 0 ||
            isStartingWorkflows ||
            saveValues.isLoading ? (
              ctx.elementId ? (
                <Loader />
              ) : (
                <Alert>Please select a scorecard</Alert>
              )
            ) : (
              <div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      flexDirection: "row",
                    }}
                  >
                    <AdminButton
                      onClick={() => {
                        listItems.forEach((elem) => {
                          toggleItem(elem);
                        });
                      }}
                    >
                      Toggle All
                    </AdminButton>
                    <AdminButton
                      onClick={() => {
                        listItems.forEach((elem) => {
                          setSelectedItems([]);
                        });
                      }}
                    >
                      Reset All
                    </AdminButton>
                    {selectedItems.length > 0 ? (
                      <>
                        <select
                          style={{
                            fontSize: "14px",
                            padding: 0,
                            margin: "2px",
                          }}
                          value={action}
                          onChange={(e) => {
                            setAction(e.target.value);
                          }}
                        >
                          <option value="" disabled>
                            Select an Action
                          </option>
                          <option value="start_workflow">Start Workflow</option>
                          <option value="delete_workflow">
                            Delete Workflow
                          </option>
                        </select>
                        {action === "start_workflow" ? (
                          <select
                            style={{
                              fontSize: "14px",
                              padding: 0,
                              margin: "2px",
                            }}
                            value={currentwfconfig}
                            onChange={(e) => {
                              setcurrentwfconfig(e.target.value);
                            }}
                          >
                            <option value="" disabled>
                              Select a WF Config
                            </option>
                            {configNames.map((cname) => (
                              <option key={cname.name} value={cname.name}>
                                {cname.name}
                              </option>
                            ))}
                          </select>
                        ) : null}
                        {action ? (
                          <AdminButton
                            onClick={async () => {
                              if (action === "start_workflow") {
                                startWorkflows({
                                  configName: currentwfconfig,
                                  period: ctx.period || "",
                                  elementIds: selectedItems
                                    .filter(
                                      (el) =>
                                        el.periodlevelid ===
                                        currentPeriod?.periodlevel
                                    )
                                    .map((el) => el.id)
                                });
                              }
                              if (action === "delete_workflow") {
                                selectedItems.forEach((item) => {
                                  try {
                                    const wf = getElementInstances(item.id);
                                    if (wf && wf.length && wf[0].wfInstanceId) {
                                      deleteWorkflowInstance(
                                        wf[0].wfInstanceId
                                      );
                                    }
                                  } catch (e) {}
                                });
                              }
                            }}
                          >
                            Execute
                          </AdminButton>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>

                <MeasureTable
                  items={listItems || []}
                  columns={[
                    {
                      title: (row) => {
                        return <div />;
                      },
                      value: (row) => (
                        <div>
                          <Checkbox
                            checked={
                              !!selectedItems.find(
                                (el) => el.id === row.elem.id
                              )
                            }
                            onChange={(value) => {
                              /** TODO */
                              toggleItem(row.elem);
                            }}
                          />
                        </div>
                      ),
                    },
                    {
                      title: "Name",
                      value: (row) => <div>{row.elem.name}</div>,
                    },
                    {
                      title: "ACT",
                      value: (row) => <div>{row.elem.ACT}</div>,
                    },
                    {
                      title: "Status",
                      sortFn: (a, b) => {
                        return Number(a.STATUS || 0) - Number(b.STATUS || 0);
                      },
                      value: (row) => (
                        <div>
                          {(() => {
                            switch (row.elem.STATUS) {
                              case "0":
                                return "Values not approved";
                              case "1":
                                return "Workflow completed";
                              default:
                                return "-";
                            }
                          })()}
                        </div>
                      ),
                    },
                    {
                      title: "Type",
                      value: (row) => <div>{row.elem.typename}</div>,
                    },
                    {
                      title: "Periodlevel",
                      value: (row) => <div>{row.elem.periodlevelname}</div>,
                    },
                    {
                      title: "Workflows",
                      value: (row) => {
                        const items = getElementInstances(row.elem.id);
                        return (
                          <div
                            style={{
                              maxHeight: "30px",
                              marginTop: 0,
                              marginBottom: 0,
                            }}
                          >
                            {(items || []).map((wfItem) => {
                              return (
                                <div
                                  style={{
                                    marginTop: "-18px",
                                  }}
                                >
                                  <PeriodChip
                                    title={`${wfItem.type} (${wfItem.currentState}) `}
                                    icon={row.elem.typename as any}
                                    period={wfItem?.period || ""}
                                  ></PeriodChip>
                                </div>
                              );
                            })}
                          </div>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )}
          </WhiteBgArea>
        </RowView>
      </StackView>
    </Page>
  );
};

export default WorkflowAdmin;
